import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Search, Users, Cake, Star, Gift, CalendarDays, ShieldCheck, Plus, X } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const money = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

/** Back office: the restaurant's client list. */
export default function ClientsPage() {
  const { token } = useOutletContext();
  const headers = useMemo(() => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }), [token]);
  const [q, setQ] = useState('');
  const [month, setMonth] = useState('');
  const [data, setData] = useState({ clients: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [adding, setAdding] = useState(false);

  const request = useCallback(async (path, options = {}) => {
    const res = await fetch(`${API}/api/merchant/clients${path}`, { headers, ...options });
    const body = await res.json().catch(() => null);
    if (!res.ok || !body) throw new Error(typeof body?.detail === 'string' ? body.detail : 'Request failed');
    return body;
  }, [headers]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ q, page_size: '50' });
      if (month) params.set('birthday_month', month);
      setData(await request(`?${params}`));
    } catch (e) {
      toast.error(e.message);
    }
    setLoading(false);
  }, [q, month, request]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div className="max-w-6xl mx-auto" data-testid="clients-page">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2"><Users className="w-6 h-6" /> Clients</h1>
          <p className="text-sm text-slate-500">{data.total} clients — from reservations, online orders and the ones you add</p>
        </div>
        <Button onClick={() => setAdding(true)} data-testid="add-client-btn"><Plus className="w-4 h-4 mr-1" /> Add client</Button>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, phone or email" className="pl-9" data-testid="client-search" />
        </div>
        <select value={month} onChange={(e) => setMonth(e.target.value)} className="h-10 rounded-md border border-slate-200 bg-white dark:bg-slate-800 px-3 text-sm" data-testid="birthday-month">
          <option value="">All birthdays</option>
          {MONTHS.map((m, i) => <option key={m} value={i + 1}>Birthday in {m}</option>)}
        </select>
      </div>

      <div className="grid lg:grid-cols-[1fr_380px] gap-4">
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <p className="p-6 text-sm text-slate-500">Loading…</p>
            ) : data.clients.length === 0 ? (
              <p className="p-6 text-sm text-slate-500">No clients yet. Guests who book a table or order online appear here.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase text-slate-400 border-b">
                  <tr><th className="p-3">Client</th><th className="p-3">Phone</th><th className="p-3 hidden md:table-cell">Visits</th><th className="p-3 hidden md:table-cell">Points</th><th className="p-3 hidden md:table-cell">Birthday</th></tr>
                </thead>
                <tbody>
                  {data.clients.map(c => (
                    <tr key={c.id} onClick={() => setSelectedId(c.id)}
                      className={`border-b last:border-0 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800 ${selectedId === c.id ? 'bg-teal-50 dark:bg-slate-800' : ''}`}
                      data-testid={`client-row-${c.id}`}>
                      <td className="p-3">
                        <div className="font-medium text-slate-900 dark:text-white flex items-center gap-1">
                          {[c.first_name, c.last_name].filter(Boolean).join(' ') || 'Guest'}
                          {c.phone_verified && <ShieldCheck className="w-3.5 h-3.5 text-teal-600" title="Phone verified" />}
                        </div>
                        <div className="text-xs text-slate-500">{c.email || ''}</div>
                      </td>
                      <td className="p-3 font-mono text-xs">{c.phone}</td>
                      <td className="p-3 hidden md:table-cell">{c.visit_count}</td>
                      <td className="p-3 hidden md:table-cell">{c.loyalty_points}</td>
                      <td className="p-3 hidden md:table-cell">{c.birthday ? new Date(`${c.birthday}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {selectedId && <ClientPanel key={selectedId} clientId={selectedId} request={request} onChanged={load} onClose={() => setSelectedId(null)} />}
      </div>

      {adding && <AddClient request={request} onClose={() => setAdding(false)} onAdded={(c) => { setAdding(false); load(); setSelectedId(c.id); }} />}
    </div>
  );
}

function ClientPanel({ clientId, request, onChanged, onClose }) {
  const [client, setClient] = useState(null);
  const [form, setForm] = useState(null);
  const [points, setPoints] = useState('');
  const [reason, setReason] = useState('');

  const load = useCallback(async () => {
    try {
      const c = await request(`/${clientId}`);
      setClient(c);
      setForm({ first_name: c.first_name || '', last_name: c.last_name || '', email: c.email || '', birthday: c.birthday || '',
                notes: c.notes || '', tags: (c.tags || []).join(', '), marketing_opt_in: c.marketing_opt_in });
    } catch (e) { toast.error(e.message); }
  }, [clientId, request]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await request(`/${clientId}`, { method: 'PATCH', body: JSON.stringify({
        first_name: form.first_name || null, last_name: form.last_name || null, email: form.email || null,
        birthday: form.birthday || null, notes: form.notes || null,
        tags: form.tags.split(',').map(t => t.trim()).filter(Boolean), marketing_opt_in: form.marketing_opt_in,
      }) });
      toast.success('Client saved');
      load(); onChanged();
    } catch (e) { toast.error(e.message); }
  };

  const adjust = async () => {
    try {
      await request(`/${clientId}/loyalty`, { method: 'POST', body: JSON.stringify({ points: Number(points), reason }) });
      setPoints(''); setReason('');
      toast.success('Points updated');
      load(); onChanged();
    } catch (e) { toast.error(e.message); }
  };

  if (!client || !form) return <Card><CardContent className="p-6 text-sm text-slate-500">Loading…</CardContent></Card>;
  const field = (key, label, type = 'text') => (
    <div>
      <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">{label}</p>
      <Input type={type} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
    </div>
  );

  return (
    <Card data-testid="client-panel">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="font-semibold text-slate-900 dark:text-white">{[client.first_name, client.last_name].filter(Boolean).join(' ') || 'Guest'}</p>
            <p className="text-xs font-mono text-slate-500">{client.phone}{client.phone_verified ? ' · verified' : ''}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-3 gap-2 text-center">
          <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-2"><p className="text-lg font-bold">{client.visit_count}</p><p className="text-[11px] text-slate-500">Visits</p></div>
          <div className="rounded-lg bg-slate-50 dark:bg-slate-800 p-2"><p className="text-lg font-bold">{money(client.total_spent_cents)}</p><p className="text-[11px] text-slate-500">Spent</p></div>
          <div className="rounded-lg bg-amber-50 dark:bg-slate-800 p-2"><p className="text-lg font-bold flex items-center justify-center gap-1"><Star className="w-4 h-4 text-amber-500" />{client.loyalty_points}</p><p className="text-[11px] text-slate-500">Points</p></div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {field('first_name', 'First name')}
          {field('last_name', 'Last name')}
          {field('email', 'Email', 'email')}
          {field('birthday', 'Birthday', 'date')}
        </div>
        {field('tags', 'Tags (comma separated)')}
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Notes</p>
          <textarea className="w-full rounded-md border border-slate-200 bg-white dark:bg-slate-800 p-2 text-sm" rows={3}
            value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-sm">Marketing texts & emails</span>
          <Switch checked={form.marketing_opt_in} onCheckedChange={(v) => setForm({ ...form, marketing_opt_in: v })} />
        </div>
        <Button onClick={save} className="w-full" data-testid="save-client-btn">Save</Button>

        <div className="border-t pt-3">
          <p className="text-sm font-semibold flex items-center gap-1 mb-2"><Star className="w-4 h-4" /> Loyalty points</p>
          <div className="flex gap-2">
            <Input type="number" placeholder="+50 / -20" value={points} onChange={(e) => setPoints(e.target.value)} className="w-28" />
            <Input placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} />
            <Button variant="outline" onClick={adjust} disabled={!points || reason.trim().length < 2}>Apply</Button>
          </div>
          {client.loyalty_history.slice(0, 5).map((e, i) => (
            <p key={i} className="text-xs text-slate-500 mt-1">{e.points > 0 ? '+' : ''}{e.points} · {e.reason}</p>
          ))}
        </div>

        <div className="border-t pt-3">
          <p className="text-sm font-semibold flex items-center gap-1 mb-2"><Gift className="w-4 h-4" /> Gift cards</p>
          {client.gift_card_error && <p className="text-xs text-amber-600">{client.gift_card_error}</p>}
          {client.gift_cards.length === 0 && !client.gift_card_error && <p className="text-xs text-slate-500">None</p>}
          {client.gift_cards.map(g => <p key={g.code} className="text-xs font-mono">{g.code} · {g.balance != null ? `$${Number(g.balance).toFixed(2)}` : ''} · {g.status}</p>)}
        </div>

        <div className="border-t pt-3">
          <p className="text-sm font-semibold flex items-center gap-1 mb-2"><CalendarDays className="w-4 h-4" /> Reservations</p>
          {client.reservations.length === 0 && <p className="text-xs text-slate-500">None</p>}
          {client.reservations.slice(0, 5).map(r => <p key={r.id} className="text-xs">{r.date} {r.time} · party of {r.party_size} · {r.status}</p>)}
        </div>

        {client.birthday && (
          <p className="text-xs text-slate-500 flex items-center gap-1"><Cake className="w-3.5 h-3.5" /> Birthday {new Date(`${client.birthday}T00:00:00`).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}</p>
        )}
      </CardContent>
    </Card>
  );
}

function AddClient({ request, onClose, onAdded }) {
  const [form, setForm] = useState({ phone: '', first_name: '', last_name: '', email: '', birthday: '' });
  const [busy, setBusy] = useState(false);
  const add = async () => {
    setBusy(true);
    try {
      const c = await request('', { method: 'POST', body: JSON.stringify({
        phone: form.phone, first_name: form.first_name || null, last_name: form.last_name || null,
        email: form.email || null, birthday: form.birthday || null,
      }) });
      toast.success('Client added');
      onAdded(c);
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <CardContent className="p-5 space-y-3">
          <p className="font-semibold">Add client</p>
          <Input placeholder="Phone *" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="new-client-phone" />
          <div className="grid grid-cols-2 gap-2">
            <Input placeholder="First name" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
            <Input placeholder="Last name" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          </div>
          <Input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <div>
            <p className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Birthday</p>
            <Input type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={add} disabled={busy || !form.phone}>Add</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
